import { Injectable } from '@angular/core';
import { UserFilterPreferences } from '@app-core/models/core.model';
import {
  UpdateSSOUserDetailsFromHeader,
  UpdateUserMetadataPayload,
  UpdateUserMetadataReqBody,
} from '@app-user-management/models/users.model';
import { UserManageService } from '@app-user-management/services/user-manage/user-manage.service';
import { UserRoleManageService } from '@app-user-management/service/user-role-manage.service';
import { AccessService } from '@app-core/services/access/access.service';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private userFilterPreferencesDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly userFilterPreferencesDebounceMs = 1000;

  constructor(
    private accessService: AccessService,
    private userManageService: UserManageService,
    private userService: UserRoleManageService
  ) {}

  public getUserFilterPreferences(): UserFilterPreferences {
    const { userMetadata = {} as any } = this.accessService.getLoginInfo();
    return (userMetadata.userFilterPreferences || {}) as UserFilterPreferences;
  }

  public getMapLayerFromUserMetadata(scope: 'liveView' | 'tripDetails' | 'requestVideo', fallbackLayer: string): string {
    return this.getUserFilterPreferences()?.[scope]?.mapLayer || fallbackLayer;
  }

  public buildUserFilterPreferencesForLayer(scope: 'liveView' | 'tripDetails' | 'requestVideo', layerId: string): UserFilterPreferences {
    const existingUserFilterPreferences = this.getUserFilterPreferences();
    return {
      ...existingUserFilterPreferences,
      [scope]: {
        ...(existingUserFilterPreferences?.[scope] || {}),
        mapLayer: layerId,
      },
    };
  }

  private getGroupedPageSizeFromUserMetadata(
    group: 'activityLog' | 'videoRequests' | 'driverManagement',
    scope: string,
    fallbackPageSize: number
  ): number {
    const groupPreferences = (this.getUserFilterPreferences()?.[group] || {}) as Record<string, { pageSize?: number }>;
    const pageSize = Number(groupPreferences[scope]?.pageSize);
    return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallbackPageSize;
  }

  private buildUserFilterPreferencesForGroupedPageSize(
    group: 'activityLog' | 'videoRequests' | 'driverManagement',
    scope: string,
    pageSize: number
  ): UserFilterPreferences {
    const existingUserFilterPreferences = this.getUserFilterPreferences();
    const groupPreferences = (existingUserFilterPreferences?.[group] || {}) as Record<string, { pageSize?: number }>;
    return {
      ...existingUserFilterPreferences,
      [group]: {
        ...groupPreferences,
        [scope]: {
          ...(groupPreferences[scope] || {}),
          pageSize,
        },
      },
    };
  }

  public getPageSizeFromUserMetadata(scope: 'activityLogList' | 'activityLogDetails', fallbackPageSize: number): number {
    return this.getGroupedPageSizeFromUserMetadata('activityLog', scope, fallbackPageSize);
  }

  public buildUserFilterPreferencesForPageSize(scope: 'activityLogList' | 'activityLogDetails', pageSize: number): UserFilterPreferences {
    return this.buildUserFilterPreferencesForGroupedPageSize('activityLog', scope, pageSize);
  }

  public getVideoRequestsPageSizeFromUserMetadata(scope: 'dvr' | 'edvr' | 'externalEvents', fallbackPageSize: number): number {
    return this.getGroupedPageSizeFromUserMetadata('videoRequests', scope, fallbackPageSize);
  }

  public buildUserFilterPreferencesForVideoRequestsPageSize(
    scope: 'dvr' | 'edvr' | 'externalEvents',
    pageSize: number
  ): UserFilterPreferences {
    return this.buildUserFilterPreferencesForGroupedPageSize('videoRequests', scope, pageSize);
  }

  public getDriverManagementPageSizeFromUserMetadata(scope: 'driver' | 'installer', fallbackPageSize: number): number {
    return this.getGroupedPageSizeFromUserMetadata('driverManagement', scope, fallbackPageSize);
  }

  public buildUserFilterPreferencesForDriverManagementPageSize(scope: 'driver' | 'installer', pageSize: number): UserFilterPreferences {
    return this.buildUserFilterPreferencesForGroupedPageSize('driverManagement', scope, pageSize);
  }

  public getFilterTypeFromUserMetadata(scope: 'assets' | 'trips' | 'incidents', fallback: string): string {
    return this.getUserFilterPreferences()?.[scope]?.filterType || fallback;
  }

  public getIncidentsPageSizeFromUserMetadata(fallback: number): number {
    const pageSize = Number(this.getUserFilterPreferences()?.incidents?.pageSize);
    return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : fallback;
  }

  public buildUserFilterPreferencesForIncidentsPageSize(pageSize: number): UserFilterPreferences {
    const existingUserFilterPreferences = this.getUserFilterPreferences();
    return {
      ...existingUserFilterPreferences,
      incidents: {
        ...(existingUserFilterPreferences?.incidents || {}),
        pageSize,
      },
    };
  }

  public buildUserFilterPreferencesForFilterType(scope: 'assets' | 'trips' | 'incidents', filterType: string): UserFilterPreferences {
    const existingUserFilterPreferences = this.getUserFilterPreferences();
    return {
      ...existingUserFilterPreferences,
      [scope]: {
        ...(existingUserFilterPreferences?.[scope] || {}),
        filterType,
      },
    };
  }

  private updateLoginInfoUserMetadata(metadata: UpdateUserMetadataPayload) {
    const loginInfo = this.accessService.getLoginInfo();
    const { userMetadata: oldMetadata = {} as any } = loginInfo;
    const mergedMetadata: UpdateUserMetadataPayload = {
      ...oldMetadata,
      ...metadata,
      userFilterPreferences: {
        ...((oldMetadata.userFilterPreferences || {}) as UserFilterPreferences),
        ...((metadata.userFilterPreferences || {}) as UserFilterPreferences),
      },
    };
    this.accessService.setLoginInfo({
      ...loginInfo,
      userMetadata: mergedMetadata as any,
    });
  }

  public updateUserFilterPreferences(userFilterPreferences: UserFilterPreferences) {
    const { userMetadata: oldMetadata = {} as any } = this.accessService.getLoginInfo();

    const mergedMetadata: UpdateUserMetadataPayload = {
      ...oldMetadata,
      userFilterPreferences: {
        ...((oldMetadata.userFilterPreferences || {}) as UserFilterPreferences),
        ...(userFilterPreferences || {}),
      },
    };

    const metadataPayloadForApi: UpdateUserMetadataPayload = {
      userFilterPreferences: mergedMetadata.userFilterPreferences,
    };

    this.updateLoginInfoUserMetadata(mergedMetadata);

    if (this.userFilterPreferencesDebounceTimer) {
      clearTimeout(this.userFilterPreferencesDebounceTimer);
    }

    this.userFilterPreferencesDebounceTimer = setTimeout(() => {
      this.syncUserFilterPreferences(metadataPayloadForApi);
      this.userFilterPreferencesDebounceTimer = null;
    }, this.userFilterPreferencesDebounceMs);
  }

  private syncUserFilterPreferences(metadataPayloadForApi: UpdateUserMetadataPayload) {
    const { loginType = '', userHash = '', userMetadata = {} as any } = this.accessService.getLoginInfo();

    if (window.self != window.top) {
      window.parent.postMessage(
        {
          type: 'user_preferences_changed',
          userPreferences: userMetadata,
        },
        '*'
      );

      if (userHash) {
        this.userService
          .updateSSOUser(encodeURIComponent(userHash), new UpdateSSOUserDetailsFromHeader(metadataPayloadForApi as any))
          .pipe(take(1))
          .subscribe(() => {});
      }
      return;
    }

    if (userHash) {
      this.userService
        .updateSSOUser(encodeURIComponent(userHash), new UpdateSSOUserDetailsFromHeader(metadataPayloadForApi as any))
        .pipe(take(1))
        .subscribe(() => {});
      return;
    }

    this.userManageService
      .updateUser(this.userId, new UpdateUserMetadataReqBody(metadataPayloadForApi), loginType)
      .pipe(take(1))
      .subscribe(
        () => {},
        () => {}
      );
  }

  public updateUserMetadata(metadata: any) {
    if (window.self != window.top) {
      const { userMetadata: oldMetadata = {} } = this.accessService.getLoginInfo();
      const userPrefChangedMessage = {
        type: 'user_preferences_changed',
        userPreferences: {
          ...oldMetadata,
          ...metadata,
        },
      };
      window.parent.postMessage(userPrefChangedMessage, '*');

      //for sso users
      const userHash = this.accessService.getLoginInfo()?.userHash || '';
      if (userHash) {
        this.userService
          .updateSSOUser(encodeURIComponent(userHash), new UpdateSSOUserDetailsFromHeader(metadata))
          .pipe(take(1))
          .subscribe(() => {});
      }
      return;
    }
    const params = {
      ...metadata,
    };
    const { loginType = '', userHash = '' } = this.accessService.getLoginInfo();
    //call update sso user if userhash is present
    if (userHash) {
      this.userService
        .updateSSOUser(encodeURIComponent(userHash), new UpdateSSOUserDetailsFromHeader(params))
        .pipe(take(1))
        .subscribe(() => {});
    } else {
      this.userManageService
        .updateUser(this.userId, new UpdateUserMetadataReqBody(params), loginType)
        .pipe(take(1))
        .subscribe(
          () => {},
          () => {}
        );
    }
  }

  get userId() {
    const loginInfo = this.accessService.getLoginInfo();
    return loginInfo?.userId;
  }
}
